import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	type ChoreographerFactory,
	type MaybeChoreographedComponentProps,
} from '@atlassian/ipm-choreographer';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import React, { useEffect } from 'react';

import { useChoreographerContext } from '../create-placement-component/providers/stable-choreographer';
import { type MessageCategory } from '../types';

const ChoreographedComponent = ({
	children,
	messageId,
	choreographerFactory,
}: React.PropsWithChildren<{ messageId: string; choreographerFactory: ChoreographerFactory }>) => {
	const { useChoreographedRender } = choreographerFactory;

	const shouldRender = useChoreographedRender({ messageId });

	if (shouldRender) {
		return <>{children}</>;
	}

	return null;
};

export const ChoreographerWrapper: (
	props: React.PropsWithChildren<{
		messageId: string;
		messageTemplateId: string;
		messageCategory?: MessageCategory;
	}>,
) => JSX.Element | null = ({ children, messageId, messageTemplateId, messageCategory }) => {
	const { factory, choreographerEnabled } = useChoreographerContext();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	useEffect(() => {
		const isTrackingChoreographedPostOfficeMessagesEnabled = FeatureGates.checkGate(
			'enable_tracking_choreographed_post_office_messages',
		);

		if (isTrackingChoreographedPostOfficeMessagesEnabled) {
			fireAnalyticsEvent(
				{
					eventType: 'operational',
					action: 'maybeChoreographed',
					actionSubject: 'postOfficeMessage',
				},
				{
					messageTemplateId,
					messageCategory,
				},
			);
		}
	}, [fireAnalyticsEvent]);

	const isChoreographedPostOfficeMessagesEnabled = FeatureGates.checkGate(
		'enable_choreographed_post_office_messages',
	);

	const useMessageTemplateIdAsChoreographerMessageId = FeatureGates.checkGate(
		'post-office_use_template_id_for_choreo',
	);

	if (choreographerEnabled && isChoreographedPostOfficeMessagesEnabled) {
		if (!factory) {
			return null;
		}

		const { shouldBeChoreographed } = factory;

		const messageType = messageCategory
			? (messageCategory.toLowerCase() as MaybeChoreographedComponentProps['messageType'])
			: undefined;
		if (
			shouldBeChoreographed({
				messageType: messageType,
			})
		) {
			// Use messageTemplateId as choreographer messageId when feature flag is enabled,
			// otherwise use the original messageId (messageInstanceId) for backward compatibility
			const choreographerMessageId = useMessageTemplateIdAsChoreographerMessageId
				? messageTemplateId
				: messageId;

			return (
				<ChoreographedComponent choreographerFactory={factory} messageId={choreographerMessageId}>
					{children}
				</ChoreographedComponent>
			);
		}
	}
	return <>{children}</>;
};
